import React, { useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import * as validators from '../../../../util/validators';
import { subscribeToNewsletter } from '../../../../util/api';
import { Button, FieldTextInput, Form } from '../../../../components';

import css from './FooterNewsletterSignup.module.css';

// Constant Contact sign_up_form email_address max length.
const EMAIL_MAX_LENGTH = 50;
const HONEYPOT_FIELD = 'b_address';

/**
 * Footer email signup: joined pill input + orange Subscribe, posts to /api/newsletter-signup.
 *
 * @component
 * @returns {JSX.Element}
 */
const FooterNewsletterSignup = () => {
  const intl = useIntl();
  const [succeeded, setSucceeded] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  if (succeeded) {
    return (
      <div className={css.root}>
        <p className={css.success} role="status">
          <FormattedMessage id="Footer.newsletterSuccess" />
        </p>
      </div>
    );
  }

  const invalidEmailMessage = intl.formatMessage({ id: 'Footer.newsletterInvalidEmail' });
  const emailRequired = validators.required(invalidEmailMessage);
  const emailValid = value => {
    const trimmed = typeof value === 'string' ? value.trim() : value;
    if (trimmed && trimmed.length > EMAIL_MAX_LENGTH) {
      return invalidEmailMessage;
    }
    return validators.emailFormatValid(invalidEmailMessage)(trimmed);
  };

  const handleSubmit = values => {
    setSubmitError(null);
    const honeypot =
      typeof values[HONEYPOT_FIELD] === 'string' ? values[HONEYPOT_FIELD].trim() : '';
    return subscribeToNewsletter({
      email: (values.email || '').trim(),
      [HONEYPOT_FIELD]: honeypot,
    })
      .then(() => {
        if (honeypot) {
          return;
        }
        setSucceeded(true);
      })
      .catch(() => {
        setSubmitError(intl.formatMessage({ id: 'Footer.newsletterError' }));
      });
  };

  return (
    <div className={css.root}>
      <FinalForm
        onSubmit={handleSubmit}
        render={formRenderProps => {
          const { handleSubmit: submitFn, submitting } = formRenderProps;
          return (
            <Form className={css.form} onSubmit={submitFn} noValidate>
              <div className={css.row}>
                <FieldTextInput
                  className={css.emailField}
                  inputRootClass={css.emailInput}
                  labelClassName={css.visuallyHidden}
                  type="email"
                  id="footer-newsletter-email"
                  name="email"
                  autoComplete="email"
                  maxLength={EMAIL_MAX_LENGTH}
                  label={intl.formatMessage({ id: 'Footer.newsletterEmailLabel' })}
                  placeholder={intl.formatMessage({ id: 'Footer.newsletterEmailPlaceholder' })}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                  hideErrorMessage
                />
                <Button type="submit" rootClassName={css.subscribe} disabled={submitting}>
                  <FormattedMessage id="Footer.newsletterSubscribe" />
                </Button>
              </div>
              <Field name={HONEYPOT_FIELD}>
                {({ input }) => (
                  <input
                    {...input}
                    className={css.honeypot}
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                )}
              </Field>
              {formRenderProps.submitFailed && formRenderProps.hasValidationErrors ? (
                <p className={css.error} role="alert">
                  <FormattedMessage id="Footer.newsletterInvalidEmail" />
                </p>
              ) : null}
              {submitError ? (
                <p className={css.error} role="alert">
                  {submitError}
                </p>
              ) : null}
              <p className={css.disclaimer}>
                <FormattedMessage id="Footer.newsletterDisclaimer" />
              </p>
            </Form>
          );
        }}
      />
    </div>
  );
};

export default FooterNewsletterSignup;
